import React from 'react'

import {
  Body,
  Caption,
  CloudinaryImage,
  Footnote,
  Spacer,
  TitleLarge,
  TitleMedium,
  TitleSmall,
} from 'ethos-design-system'

import { PreviewByline } from '../Byline/Byline'
import Markdown, { MARKDOWN_NODE_RESTRICTION_SETS } from '../global/Markdown'
import VideoEmbed from '../global/VideoEmbed'
import styles from './BlogTemplates.module.scss'

// TODO: find less verbose patterns; optimize resolutions (size, pixelation)
export type ImageStyleType =
  | 'Inline'
  | 'Medium'
  | 'FullWidth'
  | 'Small'
  | 'Video'

function BlogImage({ publicId, caption, variant, imageAlt }: any) {
  const classNames = [styles.BlogImage, styles[variant]]

  const inlineImage = (
    <CloudinaryImage
      publicId={publicId}
      width={[375, 720, 720, 720]}
      height={[250, 480, 480, 480]}
      alt={imageAlt}
    />
  )

  const mediumImage = (
    <CloudinaryImage
      publicId={publicId}
      width={[375, 688, 880, 1076]}
      height={[250, 458, 620, 620]}
      alt={imageAlt}
    />
  )

  const fullWidthImage = (
    <CloudinaryImage
      publicId={publicId}
      width={[375, 688, 880, 1296]}
      height={[250, 458, 620, 620]}
      alt={imageAlt}
    />
  )

  const smallImage = (
    <CloudinaryImage
      publicId={publicId}
      width={[279, 288, 288, 288]}
      alt={imageAlt}
    />
  )

  let image
  if (variant === BlogImage.VARIANTS.INLINE) image = inlineImage
  if (variant === BlogImage.VARIANTS.MEDIUM) image = mediumImage
  if (variant === BlogImage.VARIANTS.FULL_WIDTH) image = fullWidthImage
  if (variant === BlogImage.VARIANTS.SMALL) image = smallImage

  if (variant !== BlogImage.VARIANTS.VIDEO) {
    return (
      <figure className={classNames.join(' ')}>
        {image}
        {caption && <BlogImageCaption>{caption}</BlogImageCaption>}
      </figure>
    )
  } else {
    return (
      <div className={classNames.join(' ')}>
        <VideoEmbed youtubeId={publicId} videoTitle={caption} />
      </div>
    )
  }
}

BlogImage.VARIANTS = {
  INLINE: 'Inline',
  MEDIUM: 'Medium',
  FULL_WIDTH: 'FullWidth',
  SMALL: 'Small',
  VIDEO: 'Video',
}

function BlogImageSplit({
  publicId1,
  imageAlt1,
  caption1,
  publicId2,
  imageAlt2,
  caption2,
}: any) {
  const classNames = [styles.BlogImageSplit]

  return (
    <div className={classNames.join(' ')}>
      <figure>
        <CloudinaryImage
          publicId={publicId1}
          width={[375, 688, 880, 636]}
          height={[250, 458, 620, 424]}
          alt={imageAlt1}
        />
        {caption1 && <BlogImageCaption>{caption1}</BlogImageCaption>}
      </figure>
      <figure>
        <CloudinaryImage
          publicId={publicId2}
          width={[375, 688, 880, 636]}
          height={[250, 458, 620, 424]}
          alt={imageAlt2}
        />
        {caption2 && <BlogImageCaption>{caption2}</BlogImageCaption>}
      </figure>
    </div>
  )
}

function BlogImageCaption({ children }: { children: React.ReactNode }) {
  return (
    <figcaption>
      <Footnote.Regular400>{children}</Footnote.Regular400>
    </figcaption>
  )
}

// Previews

function BlogPreviewCopy({
  eyebrow,
  headline,
  previewBody,
  authorName,
  authorURL,
  date,
  variant,
  blogUrl,
  showLearnMore,
}: any) {
  // LARGE = 1-up
  const isLarge = variant === BlogPreviewCopy.VARIANTS.LARGE
  // SMALL = 2-up/3-up
  const isSmall = variant === BlogPreviewCopy.VARIANTS.SMALL
  // SLIM = 5-up
  const isSlim = variant === BlogPreviewCopy.VARIANTS.SLIM

  let responsiveHeadline

  if (isLarge) {
    responsiveHeadline = (
      <TitleMedium.Sans.Regular400>
        <Markdown
          input={headline}
          allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
        />
      </TitleMedium.Sans.Regular400>
    )
  }

  if (isSmall) {
    responsiveHeadline = (
      <TitleSmall.Sans.Regular400>
        <Markdown
          input={headline}
          allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
        />
      </TitleSmall.Sans.Regular400>
    )
  }

  if (isSlim) {
    responsiveHeadline = (
      <Body.Medium500>
        <Markdown
          input={headline}
          allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
        />
      </Body.Medium500>
    )
  }

  let previewBodyClass = `${styles.BlogPreviewCopy}`
  if (isSlim) {
    previewBodyClass += ` ${styles.BlogPreviewCopySlim}`
  }

  return (
    <div className={previewBodyClass}>
      <Caption.Medium500>{eyebrow}</Caption.Medium500>
      <Spacer.H16 />
      <a href={blogUrl}>{responsiveHeadline}</a>
      <div className={styles.BlogPreviewMarkdown}>
        <a href={blogUrl}>
          <Spacer.H16 />
          <Body.Regular400>
            <Markdown
              input={previewBody}
              allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
            />
          </Body.Regular400>
        </a>
      </div>
      <aside />
      <PreviewByline
        authorName={authorName}
        date={date}
        link={authorURL}
        showLearnMore={showLearnMore}
        learnMoreLink={blogUrl}
      />
    </div>
  )
}

BlogPreviewCopy.VARIANTS = {
  LARGE: 'Large',
  SMALL: 'Small',
  SLIM: 'Slim',
}

function BlogPreviewSingle({
  imagePublicId,
  imageAlt,
  blogUrl,
  ...copyProps
}: any) {
  const responsiveImage = (
    <figure>
      <a href={blogUrl}>
        <CloudinaryImage
          publicId={imagePublicId}
          width={[375, 720, 720, 856]}
          height={[250, 480, 480, 428]}
          alt={imageAlt}
        />
      </a>
    </figure>
  )

  return (
    <article className={styles.BlogPreviewSingle}>
      {responsiveImage}
      <BlogPreviewCopy
        {...copyProps}
        variant={BlogPreviewCopy.VARIANTS.LARGE}
        blogUrl={blogUrl}
      />
    </article>
  )
}
function BlogPreviewDouble({
  imagePublicId1,
  imagePublicId2,
  imageAlt1,
  imageAlt2,
  copyProps1,
  copyProps2,
  blogUrl1,
  blogUrl2,
}: any) {
  return (
    <div className={styles.BlogPreviewDouble}>
      <article>
        <figure>
          <a href={blogUrl1}>
            <CloudinaryImage
              publicId={imagePublicId1}
              alt={imageAlt1}
              width={[375, 332, 428, 636]}
              height={[250, 332, 428, 624]}
            />
          </a>
        </figure>
        <BlogPreviewCopy
          {...copyProps1}
          variant={BlogPreviewCopy.VARIANTS.SMALL}
          blogUrl={blogUrl1}
        />
        <Spacer.H72 />
      </article>
      <article>
        <figure>
          <a href={blogUrl2}>
            <CloudinaryImage
              publicId={imagePublicId2}
              alt={imageAlt2}
              width={[375, 332, 428, 636]}
              height={[250, 332, 428, 624]}
            />
          </a>
        </figure>
        <BlogPreviewCopy
          {...copyProps2}
          variant={BlogPreviewCopy.VARIANTS.SMALL}
          blogUrl={blogUrl2}
        />
        <Spacer.H72 />
      </article>
    </div>
  )
}

function BlogPreviewTriple({
  imagePublicId1,
  imagePublicId2,
  imagePublicId3,
  imageAlt1,
  imageAlt2,
  imageAlt3,
  copyProps1,
  copyProps2,
  copyProps3,
  blogUrl1,
  blogUrl2,
  blogUrl3,
  heading,
  displayHeading,
  showRoundedCorners,
}: any) {
  // Such verbose
  // Very verbose

  const triplePreviewStyles = [styles.BlogPreviewTriple]
  if (showRoundedCorners) {
    triplePreviewStyles.push(styles.roundedCorners)
  }

  return (
    <div>
      {displayHeading && (
        <div>
          <TitleLarge.Serif.Book500>
            <Markdown
              input={heading}
              allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
            />
          </TitleLarge.Serif.Book500>
        </div>
      )}
      <div className={triplePreviewStyles.join(' ')}>
        <article>
          <figure>
            <a href={blogUrl1}>
              <CloudinaryImage
                publicId={imagePublicId1}
                alt={imageAlt1}
                width={[375, 240, 277, 416]}
                height={[250, 160, 185, 277]}
              />
            </a>
          </figure>
          <BlogPreviewCopy
            {...copyProps1}
            variant={BlogPreviewCopy.VARIANTS.SMALL}
            blogUrl1={blogUrl1}
          />
        </article>
        <article>
          <figure>
            <a href={blogUrl2}>
              <CloudinaryImage
                publicId={imagePublicId2}
                alt={imageAlt2}
                width={[375, 240, 277, 416]}
                height={[250, 160, 185, 277]}
              />
            </a>
          </figure>
          <BlogPreviewCopy
            {...copyProps2}
            variant={BlogPreviewCopy.VARIANTS.SMALL}
            blogUrl2={blogUrl2}
          />
        </article>
        <article>
          <figure>
            <a href={blogUrl3}>
              <CloudinaryImage
                publicId={imagePublicId3}
                alt={imageAlt3}
                width={[375, 240, 277, 416]}
                height={[250, 160, 185, 277]}
              />
            </a>
          </figure>
          <BlogPreviewCopy
            {...copyProps3}
            variant={BlogPreviewCopy.VARIANTS.SMALL}
            blogUrl3={blogUrl3}
          />
        </article>
      </div>
    </div>
  )
}

function BlogPreviewSlimItem({
  imagePublicId,
  imageAlt,
  blogUrl,
  ...copyProps
}: any) {
  return (
    <article>
      <figure>
        <a href={blogUrl}>
          <CloudinaryImage
            publicId={imagePublicId}
            width={[375, 240, 277, 240]}
            height={[250, 160, 185, 337]}
            alt={imageAlt}
          />
        </a>
      </figure>
      <BlogPreviewCopy
        {...copyProps}
        variant={BlogPreviewCopy.VARIANTS.SLIM}
        blogUrl={blogUrl}
      />
    </article>
  )
}

function BlogPreviewSlim({ articles = [], heading, displayHeading }: any) {
  return (
    <div className={styles.BlogPreviewSlim}>
      {displayHeading && (
        <div className={styles.BlogPreviewSlimHeading}>
          <TitleMedium.Serif.Demi600>
            <Markdown
              input={heading}
              allowedMarkdownTypes={MARKDOWN_NODE_RESTRICTION_SETS.LINK_ONLY}
            />
          </TitleMedium.Serif.Demi600>
        </div>
      )}
      <div className={styles.BlogPreviewSlimItems}>
        {articles.map((a: any, i: number) => (
          <BlogPreviewSlimItem {...a} key={a.headline + i} />
        ))}
      </div>
    </div>
  )
}

// Exports & text components

export const Blog = {
  Image: {
    Inline: (props: any) => (
      <BlogImage {...props} variant={BlogImage.VARIANTS.INLINE} />
    ),
    Medium: (props: any) => (
      <BlogImage {...props} variant={BlogImage.VARIANTS.MEDIUM} />
    ),
    FullWidth: (props: any) => (
      <BlogImage {...props} variant={BlogImage.VARIANTS.FULL_WIDTH} />
    ),
    Small: (props: any) => (
      <BlogImage {...props} variant={BlogImage.VARIANTS.SMALL} />
    ),
    Video: (props: any) => (
      <BlogImage {...props} variant={BlogImage.VARIANTS.VIDEO} />
    ),
  },
  ImageSplit: (props: any) => <BlogImageSplit {...props} />,
  Intro: ({ paragraphs = [] }: any) => (
    <div className={styles.BlogIntro}>
      {paragraphs.map((p: string, i: number) => (
        <TitleSmall.Sans.Regular400 key={p + i}>{p}</TitleSmall.Sans.Regular400>
      ))}
    </div>
  ),
  Story: ({ children }: any) => (
    <div className={styles.BlogStory}>{children}</div>
  ),
  Blockquote: ({ children }: any) => (
    <blockquote className={styles.BlogBlockquote}>{children}</blockquote>
  ),
  Preview: {
    Single: (props: any) => <BlogPreviewSingle {...props} />,
    Double: (props: any) => <BlogPreviewDouble {...props} />,
    Triple: (props: any) => <BlogPreviewTriple {...props} />,
    Slim: (props: any) => <BlogPreviewSlim {...props} />,
  },
}
